import React, { useCallback } from 'react';
import { styled, css } from '@compiled/react';
import BoardIcon from '@atlaskit/icon/glyph/board';
import { colors } from '@atlaskit/theme';
import { fontSize, fontSizeSmall } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import type { Command } from '@atlassian/jira-command-palette';
import { ff } from '@atlassian/jira-feature-flagging';
import { useTransformNavigationCommands } from '../../../utils/transform-navigation-commands';
import type { BoardCommandProps } from './types';

export const useGetBoardCommand = () => {
	const { transformNavigationCommand } = useTransformNavigationCommands();

	const BoardNameText = ff('replace-typography-css-with-tokens_ifsw8')
		? BoardNameTextNew
		: BoardNameTextOld;

	const ContainerNameText = ff('replace-typography-css-with-tokens_ifsw8')
		? ContainerNameTextNew
		: ContainerNameTextOld;

	const renderBoardName = useCallback(
		(name: string, containerName?: string, isActive?: boolean) => (
			<BoardNameContainer data-testid="command-palette-common.common.ui.command.board.board-name-container">
				<BoardNameText>{name}</BoardNameText>
				{containerName && <ContainerNameText active={isActive}>{containerName}</ContainerNameText>}
			</BoardNameContainer>
		),
		[BoardNameText, ContainerNameText],
	);

	const getBoardCommand = useCallback(
		({ board, keywords, section, analytics, ...rest }: BoardCommandProps): Command => {
			const { id, url, name, containerId = 'UNAVAILABLE', containerName } = board;

			return transformNavigationCommand(
				{
					id: `board-${id}`,
					section,
					name,
					keywords,
					components: {
						Name: ({ isActive }) => renderBoardName(name, containerName, isActive),
						LeftIcon: () => <BoardIcon label="" />,
					},
					analytics: {
						action: 'boardViewed',
						boardId: id,
						boardContainerId: containerId || '',
						metadata: {
							contentId: id,
							containerId,
							contentType: 'board',
						},
						...analytics,
					},
					...rest,
				},
				{ path: url },
			);
		},
		[renderBoardName, transformNavigationCommand],
	);

	return { getBoardCommand };
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardNameContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075', '6px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardNameTextOld = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: `${fontSize()}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardNameTextNew = styled.div({
	font: token('font.body'),
	lineHeight: 'inherit',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContainerNameTextOld = styled.div<{ active?: boolean }>(
	{
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: `${fontSizeSmall()}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ active }) =>
		active
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtle', colors.N400),
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtlest', colors.N200),
				}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContainerNameTextNew = styled.div<{ active?: boolean }>(
	{
		font: token('font.body.small'),
		lineHeight: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ active }) =>
		active
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtle', colors.N400),
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: token('color.text.subtlest', colors.N200),
				}),
);
